import {HttpClient, HttpHeaders, HttpParams,} from '@angular/common/http';
import {Injectable,} from '@angular/core';
import {AsyncStatusVO, AsyncThreadIdVO, MessageLevel, MessageVO,} from '@models/index';
import {Logger, MessageStackService, SystemConstant, WindowService,} from '@services/index';
import {catchError, filter, interval, map, Observable, switchMap, takeWhile,} from 'rxjs';

export interface IRequestOptions {
  headers?: HttpHeaders;
  observe?: 'body';
  params?: HttpParams;
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

/**
 * HttpClientAsyncサービス.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpClientAsync {

  private statusUrl = '/async/response';

  constructor(
    private logger: Logger,
    private httpClient: HttpClient,
    private windowService: WindowService
  ) {}

  /**
   * [一般開発者利用可]
   * POSTリクエストに「async=true」のパラメーターをアタッチし、リクエストを送信して、
   * サーバーからスレッド ID を受け取る。
   * 「/async/response」API は、最初の API 呼び出しのステータスが完了するまで、
   * スレッド ID を介して定期的に呼び出される。
   *
   * @param url 文字列 URL の相対パス
   * @param body リクエストのコンテンツ
   * @param options リクエストと共に送信する HTTP オプション。
   * @returns リクエストに対する `HttpResponse` の `Observable` で、リクエストされたタイプのレスポンス ボディを持つ。
   */
  // eslint-disable-next-line
  public post<T>(url: string, body: any | null, options?: IRequestOptions): Observable<T> {
    this.logger.debug('■POSTの非同期処理を実行中');

    if(body == null) {
      const tempBody = new AsyncThreadIdVO();
      tempBody.asyncThreadId = 'CSI09001';
      body = tempBody;
      this.logger.debug(body);
    }

    const intevalSec = this.getParamAysncInterval(options);
    const intervalMSec = intevalSec * 1000;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.httpClient.post<any>(url, body, this.getRequestOptions(options)).pipe(
      switchMap((procResponse) => {
        return interval(intervalMSec).pipe(
          switchMap((i) => {
            const threadIdVo = Object.assign(new AsyncThreadIdVO(), procResponse);
            this.logger.debug(i + ':threadID:' + threadIdVo.asyncThreadId);
            return this.httpClient.post<AsyncStatusVO>(this.statusUrl, threadIdVo);
          }),
          takeWhile((resp: AsyncStatusVO) => {
            return !this.isStatusCompleted(resp);
          }, true),
          map((resp: AsyncStatusVO) => {
            if (this.isStatusCompleted(resp)) {
              this.windowService.asyncTransactionFlag = false;
              this.logger.debug('⇒ 〇 処理済 〇');
              this.logger.debug('asyncStatus:' + resp.asyncStatus + ', httpStatus:' + resp.httpStatus);
              return resp.asyncResponse;
            } else if (resp.httpStatus >= 400) {
              this.windowService.asyncTransactionFlag = false;
              if (resp.asyncResponse) {
                const messageStack = new MessageStackService(MessageLevel.ERROR);
                resp.asyncResponse.forEach((e: MessageVO) => {
                  if (e.messageId) {
                    messageStack.add(e);
                  } else {
                    messageStack.add({messageId: 'CSS01999M001E',});
                  }
                });
                throw messageStack;
              } else {
                this.logger.errorWithFreeText('APIコール時にサーバーサイドでエラーが発生');
                this.logger.debug('asyncStatus:' + resp.asyncStatus + ', httpStatus:' + resp.httpStatus);
                const messageStack = new MessageStackService(MessageLevel.ERROR);
                messageStack.add({messageId: 'CSS01999M001E',});
                throw messageStack;
              }
            } else {
              this.logger.debug('⇒ 処理中・・・');
              this.logger.debug('asyncStatus:' + resp.asyncStatus + ', httpStatus:' + resp.httpStatus);
            }
          }),
          // eslint-disable-next-line
          filter(resp => resp !== undefined),
          catchError((e) => {
            this.windowService.asyncTransactionFlag = false;
            this.logger.errorWithFreeText(e);
            const messageStack = new MessageStackService(MessageLevel.ERROR);
            if (e != null && e.messages != null && e.messages.length > 0 && e.messages[0].messageId != null) {
              messageStack.add(e.messages[0]);
            } else {
              messageStack.add({messageId: 'CSS01999M001E',});
            }
            throw messageStack;
          })
        )
      })
    );
  }

  /**
   * [一般開発者利用可]
   * GETリクエストに「async=true」のパラメーターをアタッチし、リクエストを送信して、
   * サーバーからスレッド ID を受け取る。
   * 「/async/response」API は、最初の API 呼び出しのステータスが完了するまで、
   * スレッド ID を介して定期的に呼び出される。
   *
   * @param url 文字列 URL の相対パス
   * @param body リクエストのコンテンツ
   * @param options リクエストと共に送信する HTTP オプション。
   * @returns リクエストに対する `HttpResponse` の `Observable` で、リクエストされたタイプのレスポンス ボディを持つ。
   */
  public get<T>(url: string, options?: IRequestOptions): Observable<T> {
    this.logger.debug('■GETの非同期処理を実行中');

    const intevalSec = this.getParamAysncInterval(options);
    const intervalMSec = intevalSec * 1000;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.httpClient.get<any>(url, this.getRequestOptions(options)).pipe(
      switchMap((procResponse) => {
        return interval(intervalMSec).pipe(
          switchMap((i) => {
            const threadIdVo = Object.assign(new AsyncThreadIdVO(), procResponse);
            this.logger.debug(i + ':threadID:' + threadIdVo.asyncThreadId);
            return this.httpClient.post<AsyncStatusVO>(this.statusUrl, threadIdVo);
          }),
          takeWhile((resp: AsyncStatusVO) => {
            return !this.isStatusCompleted(resp);
          }, true),
          map((resp: AsyncStatusVO) => {
            if (this.isStatusCompleted(resp)) {
              this.windowService.asyncTransactionFlag = false;
              this.logger.debug('⇒ 〇 処理済 〇');
              this.logger.debug('asyncStatus:' + resp.asyncStatus + ', httpStatus:' + resp.httpStatus);
              return resp.asyncResponse;
            } else if (resp.httpStatus >= 400) {
              this.windowService.asyncTransactionFlag = false;
              if (resp.asyncResponse) {
                const messageStack = new MessageStackService(MessageLevel.ERROR);
                resp.asyncResponse.forEach((e: MessageVO) => {
                  if (e.messageId) {
                    messageStack.add(e);
                  } else {
                    messageStack.add({messageId: 'CSS01999M001E',});
                  }
                });
                throw messageStack;
              } else {
                this.logger.errorWithFreeText('APIコール時にサーバーサイドでエラーが発生');
                this.logger.debug('asyncStatus:' + resp.asyncStatus + ', httpStatus:' + resp.httpStatus);
                const messageStack = new MessageStackService(MessageLevel.ERROR);
                messageStack.add({messageId: 'CSS01999M001E',});
                throw messageStack;
              }
            } else {
              this.logger.debug('⇒ 処理中・・・');
              this.logger.debug('asyncStatus:' + resp.asyncStatus + ', httpStatus:' + resp.httpStatus);
            }
          }),
          // eslint-disable-next-line
          filter(resp => resp !== undefined),
          catchError((e) => {
            this.windowService.asyncTransactionFlag = false;
            this.logger.errorWithFreeText(e);
            const messageStack = new MessageStackService(MessageLevel.ERROR);
            messageStack.add({messageId: 'CSS01999M001E',});
            throw messageStack;
          })
        )
      })
    );
  }

  /**
   * [一般開発者利用不可]
   * takeWhileとmap で使用する。APIコールのポーリングをいつ停止するかを決定する。
   * @param asyncStatusVO AsyncStatusVO オブジェクト
   */
  private isStatusCompleted(asyncStatusVO: AsyncStatusVO): boolean {
    if (asyncStatusVO && asyncStatusVO.asyncStatus === '1' && asyncStatusVO.httpStatus === 200) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * [一般開発者利用不可]
   * リクエスト オプションに 「async=true」 パラメータを追加する。
   * @param options HTTP オプション
   */
  private getRequestOptions(options?: IRequestOptions | undefined): IRequestOptions {
    const asyncParams = new HttpParams()
      .set('async', true);
    if(options) {
      options.params = options.params?.append('async', true);
      return options;
    } else {
      const opt: IRequestOptions = {params: asyncParams,};
      return opt;
    }
  }

  /**
   * [一般開発者利用不可]
   * HTTP オプションから「asyncInterval」の値を抽出する。 指定しない場合、
   * SystemConstant.ASYNC_INTERVAL_IN_SECONDS を返却する。
   * @param options HTTP オプション
   */
  private getParamAysncInterval(options?: IRequestOptions | undefined): number {
    if(options && options.params) {
      const sec = options.params.get('asyncInterval') || SystemConstant.ASYNC_INTERVAL_IN_SECONDS;
      return parseInt(sec);
    } else {
      return parseInt(SystemConstant.ASYNC_INTERVAL_IN_SECONDS);
    }
  }
}
